// Adapted from example code at https://mui.com/material-ui/react-table/

import { dequal } from 'dequal';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TablePagination,
  TableRow, Paper, Checkbox, IconButton, Tooltip, Grid, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AssessmentIcon from '@material-ui/icons/AssessmentSharp';
import DeleteIcon from '@material-ui/icons/DeleteSharp';
import FavoriteNIcon from '@material-ui/icons/FavoriteBorderSharp';
import FavoriteYIcon from '@material-ui/icons/FavoriteSharp';
import DownloadIcon from '@material-ui/icons/GetAppSharp';
import LockOpenIcon from '@material-ui/icons/LockOpenSharp';

import { StitchIcon } from './Icons';

import SelectionToolbar from './SelectionToolbar';
import { EnhancedTableHead } from './EnhancedTableToolbar';
import DeleteModal from './DeleteModal';
import ShareModal from './ShareModal';
import StitchModal from './StitchModal';
import StitchViewModal from './StitchViewModal';
import TagList from './TagList';
import { AppContext } from './AppState';
import { useUser, useUserApi, useAllUsers } from '../utils/users';
import { applySelect } from '../utils/uiLib';
import { formatDate, folderIsChild, folderMatches } from '../utils/formatLib';
import { materialSrl } from '../utils/formatAppLib';
import { collectTags, getSelectedMats } from '../utils/materialsLib';
import { getComparator, getComparatorFolders, getUserVal, stableSort } from '../utils/tableLib';
import { USER_TILING_INCLUDED } from '../utils/users';
import CopyMidButton from './CopyMidButton';

// Set to true to show a "select all" check-box (if allowSelection is also true)
const allowSelectAll = true;

const useStyles = makeStyles((theme) => ({
  buttonCell: {
    maxWidth: '120px',
  },
  idText: {
    fontFamily: 'monospace',
  },
  linkText: {
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.light,
      textDecoration: 'underline',
    },
  },
  iconButton: {
    paddingTop: '0px',
    paddingBottom: '0px',
    height: '0.9em',
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(-0.5),
  },
  padLeft: {
    paddingLeft: theme.spacing(1),
  },
  folderSmall: {
    height: '16px',
  },
  subtleText: {
    color: theme.palette.secondary.light,
  },
  centred: {
    textAlign: 'center',
  },
}));

export default function MaterialTable(props) {
  const {
    materials, columns, showTags, buttons, folder, setFolder, folders, filterByFolder, onMoveFolder,
    deleteMaterial, putFields, reloadMaterial, onSelectedTagsChanged,
    onDelete, allowSelection, showOnlyVisible, order, setOrder, orderBy, setOrderBy, handleOrderByChange,
  } = props;

  const { userItem, error } = useUser();
  const { allUserItems } = useAllUsers();
  const { putUiSettings } = useUserApi();

  const [listViewLoaded, setListViewLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [userTableSettingsLoaded, setUserTableSettingsLoaded] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [tableCols, setTableCols] = useState(null);
  const [users, setUsers] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [modalMaterialId, setModalMaterialId] = useState(null);
  const [showStitchModal, setShowStitchModal] = useState(false);
  const [stitchViewModalMatProxy, setStitchViewModalMatProxy] = useState(null);
  const [tagHovers, setTagHovers] = useState(new Map());
  const [materialTags, setMaterialTags] = useState({});

  const {
    setGalleryId, setRedirect, setFilteredMaterials, qaDisplay, showAlert,
    listViewOrder, setListViewOrder, listViewOrderBy, setListViewOrderBy, listViewPage, setListViewPage,
    selected, setSelected, lastSelected, setLastSelected, galleryUser, userTags,
  } = useContext(AppContext);

  const classes = useStyles();

  useEffect(() => {
    if (!userItem || userTableSettingsLoaded) {
      return;
    }
    if (error) {
      console.warn('Could not load user settings');
    }
    if (userItem?.uiSettings?.tableRowsPerPage) {
      setRowsPerPage(userItem.uiSettings.tableRowsPerPage);
    }
    setUserTableSettingsLoaded(true);
  }, [userItem, error, userTableSettingsLoaded]);

  useEffect(() => {
    if (!listViewLoaded) {
      // On load: If there's a saved list state, restore it now
      if (listViewOrder !== null && listViewOrderBy !== null) {
        setOrder(listViewOrder);
        setListViewOrder(null);
      }
      if (listViewOrderBy !== null) {
        setOrderBy(listViewOrderBy);
        setListViewOrderBy(null);
      }
      if (listViewPage !== -1) {
        setPage(listViewPage);
        setListViewPage(-1);
      }
      setListViewLoaded(true);
    }
  }, [listViewLoaded, listViewOrder, listViewOrderBy, listViewPage, setListViewOrder, setListViewOrderBy, setListViewPage]);

  useEffect(() => {
    var colsToUse;
    if (columns) {
      colsToUse = columns;
    } else {
      // Default table columns if none supplied
      colsToUse = [
        'name',
        'text',
        'status',
        'createdAt',
      ];
    }
    setTableCols(colsToUse);

    var userFieldPresent = false;
    for (var i = 0; i < colsToUse.length; i++) {
      if (colsToUse[i].startsWith('user.')) {
        userFieldPresent = true;
        break;
      }
    }
    if (userFieldPresent && !users) {
      setUsers(allUserItems);
    }
  }, [columns, allUserItems, users]);

  const filteredMats = materials ? (showOnlyVisible ? materials.filter(m => m.visible) : materials) : [];
  const visibleMats = filterByFolder ? filteredMats.filter(m => folderMatches(m.folder, folder)) : filteredMats;
  const sortedMats = stableSort(visibleMats, getComparator(order, orderBy, users));

  const filteredFolders = folders ? folders.filter(f => materials.filter(m => m.folder === f && m.visible).length) : [];
  const visibleFolders = folders ? filteredFolders.filter(f => folderIsChild(folder, f)) : [];
  const sortedFolders = (materials && folders) ? stableSort(
    visibleFolders.filter(f => materials.filter(m => m.folder === f && m.visible).length),
    getComparatorFolders(order, orderBy)
  ) : [];

  useEffect(() => {
    setMaterialTags((prevMaterialTags) => {
      const newMaterialTags = {};
      for (const mat of materials ?? []) {
        const oldTags = prevMaterialTags[mat.materialId];
        const newTags = collectTags([ mat ], USER_TILING_INCLUDED);
        newMaterialTags[mat.materialId] = (dequal(oldTags, newTags)) ? oldTags : newTags;
      }
      if (dequal(prevMaterialTags, newMaterialTags)) {
        return prevMaterialTags;
      } else {
        return newMaterialTags;
      }
    });
  }, [materials]);

  const onTagHovers = useCallback((hover, materialId) => {
    setTagHovers(map => new Map(map).set(materialId, hover));
  }, []);

  const anyTagHovers = visibleMats.filter(m => tagHovers.get(m.materialId)).length > 0;

  useEffect(() => {
    // Ensure we're not paginated off the end of the list (e.g. due to a change in filtering)
    const visibleItems = visibleMats.length + visibleFolders.length;
    if (page * rowsPerPage > visibleItems) {
      setPage(Math.floor((visibleItems - 1) / rowsPerPage));
    }
  }, [page, rowsPerPage, visibleFolders.length, visibleMats.length]);

  async function handlePublish(materialId, data) {
    await reloadMaterial(materialId, { publish: data });
  }

  async function handleUnlock(materialId) {
    await reloadMaterial(materialId, { unlocked: true });
  }

  // If a modal is open then get the latest copy of the material for the modal.
  const modalMaterial = (modalMaterialId && (showPublishModal || showDeleteModal)) ? (
    materials.find(m => m.materialId === modalMaterialId)
  ) : null;

  function onClickGoto(target, event, material) {
    // if (event.button !== 0) return;

    // Avoid the click selecting the material
    event.stopPropagation();

    // Put the gallery materials in our sorted order, and save the list state for return to list view
    setFilteredMaterials(sortedMats);
    setListViewOrder(order);
    setListViewOrderBy(orderBy);
    setListViewPage(page);

    // Set material to open with, and redirect
    setGalleryId(material.materialId);
    setRedirect(target);
  }

  async function onClickFavourite(event, material) {
    event.stopPropagation();
    const newVal = !material.favourite;
    putFields({ [material.materialId]: { favourite: newVal } });
  }

  function onClickPublish(event, materialId) {
    event.stopPropagation();
    setModalMaterialId(materialId);
    setShowPublishModal(true);
  }

  function onClickDelete(event, materialId) {
    event.stopPropagation();
    setModalMaterialId(materialId);
    setShowDeleteModal(true);
  }

  async function doOnDelete() {
    var res = false;
    if (modalMaterialId) {
      res = await deleteMaterial(modalMaterialId);
    }
    setModalMaterialId(null);
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete(modalMaterialId);
    }
    return res;
  }

  const handleSelectAll = (event) => {
    // Select all materials visible according to current filter
    if (event.target.checked) {
      const newSelected = visibleMats.map((n) => n.materialId);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (event, mid) => {
    if (allowSelection) {
      const { newSelected, selectedMid } = applySelect(event, mid, sortedMats, selected, lastSelected, folder);
      setSelected(newSelected);
      setLastSelected(selectedMid);
    }
  };

  const processingActions = {
    bach: ['Scan processing', 'Scan processed', 'Queued (preparing scan report)', 'Queued'],
    bagit: ['Preparing scan report', 'Scan report ready'],
    bake: ['Baking textures', 'Textures baked'],
    copy: ['Copying', 'Copied'],
    move: ['Moving', 'Moved'],
    texport: ['Texture processing', 'Textures processed'],
    tiling: ['Tiling', 'Tiled'],
    stitching: ['Stitching', 'Stitched'],
    texin: ['Importing', 'Imported'],
  };

  function getCellContent(material, column, users, midLink) {
    let content = '';
    if (users && column.startsWith('user.')) {
      content = getUserVal(material, column, users);
    } else {
      let val = material[column];
      if (column === 'materialId') {
        content = (
          <Box className={classes.idText}>
            {materialSrl(val).slice(6, 14)}
            {midLink && (
              <CopyMidButton
                material={material}
                showAlert={showAlert}
                className={`${classes.iconButton} ${classes.padLeft}`}
              />
            )}
          </Box>
        );
      } else if (column === 'createdAt') {
        content = formatDate(val);
      } else if (column === 'success') {
        if (['processing', 'queued'].includes(material?.status)) {
          content = '';
        } else if (val) {
          content = 'OK';
        } else if (val === false) {
          content = 'Failed';
        } else {
          // If success is undefined, the material hasn't been processed yet and we actually want to show
          // nothing rather than "Failed".
          content = '';
        }
      } else if (column === 'status') {
        if (val === 'processed' && material?.qaStatus === 'qa') {
          content = 'qa';
        } else if (val === 'queued' && material?.processingAction === 'bach') {
          const index = (!material.triageUserPath) ? 2 : 3;
          content = processingActions['bach'][index] ?? 'Processing';
        } else if (val === 'processing' && material?.processingAction) {
          const key = (material.processingAction === 'bach' && !material.triageUserPath) ? 'bagit' : material.processingAction;
          content = processingActions[key][0] ?? 'Processing';
        } else if (val === 'processed' && material?.processingAction) {
          content = processingActions[material.processingAction][1] ?? 'Processed';
        } else {
          content = val;
        }

        if (content === 'qa') {
          content = 'QA';
        } else if (content === 'scan') {
          content = 'New scan';
        } else if (content) {
          // - Insert space before each capital letter
          content = content.replace(/([A-Z])/g, ' $1').trim();
          // - Capitalise first letter and de-capitalise the other letters
          content = content.charAt(0).toUpperCase() + content.slice(1).toLowerCase();
        }
      } else {
        content = val;
      }
    }
    return content;
  }

  function getFolderCellContent(folder, column, numMaterials) {
    let content = '';
     if (column === 'name') {
      content = folder;
    } else if (column === 'text') {
      content = <Typography variant='body2' className={classes.subtleText}>
        {`${numMaterials} material${numMaterials !== 1 ? 's' : ''}`}
      </Typography>
    }

    return content;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    putUiSettings(userItem.userId, { tableRowsPerPage: rows });
    setPage(0);
  };

  const isSelected = (id) => {
    return id && selected.indexOf(id) !== -1;
  };

  const isClickableName = (row, col) => {
    return (col == 'name' && ['qa', 'processed'].includes(row.status) && row.success);
  }

  const isClickableFolderName = (col) => {
    return (col == 'name');
  }

  const handleClickFolder = (row) => {
    setFolder(row);
  }

  const handleMoveFolder = async (target) => {
    await onMoveFolder(target, getSelectedMats(materials, selected));
    setSelected([]);                   // Clear selection
  }

  const handleTagsChanged = useCallback(async (materialId, tagsChanged) => {
    const { selected } = tagsChanged;
    putFields({ [materialId]: { tags: selected } });
  }, [putFields]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = (page > 0 && visibleMats) ? Math.max(0, (1 + page) * rowsPerPage - visibleMats.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {visibleMats && sortedMats && (
          <>
            {allowSelection && (
              <SelectionToolbar
                setShowStitchModal={setShowStitchModal}
                folder={folder}
                setFolder={setFolder}
                folders={folders}
                onMoveFolder={handleMoveFolder}
                onTagsChanged={onSelectedTagsChanged}
                materials={materials}
                inListView={true}
                order={order}
                orderBy={orderBy}
              />
            )}
            <TableContainer>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={'small'}
              >
                <EnhancedTableHead
                  tableCols={tableCols}
                  buttons={buttons}
                  showTags={showTags}
                  allowSelection={allowSelection}
                  allowSelectAll={allowSelectAll}
                  onSelectAllClick={handleSelectAll}
                  order={order}
                  orderBy={orderBy}
                  numSelected={allowSelection ? selected.length : 0}
                  rowCount={visibleMats.length}
                  onRequestSort={handleOrderByChange}
                  iconCol={buttons.includes('stitchView')}
                />
                <TableBody>
                  {sortedFolders.slice(
                    Math.min(page * rowsPerPage, sortedFolders.length),
                    Math.min((page + 1) * rowsPerPage, sortedFolders.length)
                  ).map((row, index) => {
                    const numMaterials = filteredMats.filter(m => m.folder === row).length;
                    if (numMaterials === 0) {
                      return <React.Fragment key={index} />;
                    }
                    return (
                      <TableRow key={row} tabIndex={-1} hover>
                        {allowSelection && (
                          <TableCell padding='checkbox' />
                        )}
                        <TableCell padding='checkbox'>
                          <img
                            src='folder.512x410.png'
                            className={classes.folderSmall}
                          />
                        </TableCell>
                        {columns.map((col, i) =>
                          <TableCell
                            key={i}
                            className={isClickableFolderName(col) ? classes.linkText : ''}
                            onClick={isClickableFolderName(col) ? () => handleClickFolder(row) : undefined}
                          >
                            {getFolderCellContent(row, col, numMaterials)}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {sortedMats.slice(
                    Math.max(page * rowsPerPage - sortedFolders.length, 0),
                    Math.max((page + 1) * rowsPerPage - sortedFolders.length, 0)
                  )
                    .map((row, index) => {
                      const isItemSelected = allowSelection && isSelected(row.materialId);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          hover
                          onClick={(event) => !anyTagHovers && handleSelect(event, row.materialId)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          id={row.materialId}
                          key={row.materialId}
                          selected={isItemSelected}
                        >
                          {allowSelection && (
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{
                                  'aria-labelledby': labelId,
                                }}
                              />
                            </TableCell>
                          )}
                          {buttons.includes('stitchView') && (
                            <TableCell padding='checkbox'>
                              {row.stitched ?
                                <Tooltip title='View stitched material details'>
                                  <span>
                                    <IconButton
                                      className={classes.iconButton}
                                      onClick={(event) => {
                                        const { userId: materialUserId, materialId } = row;
                                        setStitchViewModalMatProxy({ materialUserId, materialId });
                                        event.stopPropagation();
                                      }}
                                    >
                                      <StitchIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                : ''
                              }
                            </TableCell>
                          )}
                          {columns.map((col, i) =>
                            <TableCell
                              key={i}
                              className={isClickableName(row, col) ? classes.linkText : ''}
                              onClick={isClickableName(row, col) ? (event) => onClickGoto('/view', event, row) : undefined}
                            >
                              {getCellContent(row, col, users, qaDisplay)}
                            </TableCell>
                          )}
                          {showTags && (
                            <TableCell key={88}>
                              {materialTags?.[row.materialId] && (
                                <TagList
                                  key={row.materialId}
                                  materialId={row.materialId}
                                  tags={materialTags[row.materialId]}
                                  onChange={handleTagsChanged}
                                  tagChoices={userTags}
                                  alwaysExpanded={false}
                                  onHover={onTagHovers}
                                  hideOverlay={true}
                                />
                              )}
                            </TableCell>
                          )}
                          {buttons && (
                            <TableCell key={99} className={classes.buttonCell}>
                              <Grid container spacing={1}>
                                <Grid item xs={3}>
                                  {buttons.includes('report') && row.triageUserPath && (
                                    <Tooltip title='View scan report'>
                                      <IconButton
                                        id='button'
                                        color='secondary'
                                        size='small'
                                        className={classes.iconButton}
                                        onClick={(event) => onClickGoto('/report', event, row)}
                                      >
                                        <AssessmentIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <Grid item xs={3}>
                                  {buttons.includes('publish') && (
                                    <Tooltip
                                      title={(row.unlocked ? 'Download / share' : 'Unlock')}
                                    >
                                      <IconButton
                                        id='button'
                                        color='secondary'
                                        size='small'
                                        className={classes.iconButton}
                                        onClick={(event) => onClickPublish(event, row.materialId)}
                                      >
                                        {row.unlocked ? <DownloadIcon /> : <LockOpenIcon />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <Grid item xs={3}>
                                  {buttons.includes('favourite') && (
                                    <Tooltip
                                      title={(row.favourite ? 'Un-f' : 'F') + 'avourite'}
                                    >
                                      <IconButton
                                        id='button'
                                        color='secondary'
                                        size='small'
                                        className={classes.iconButton}
                                        onClick={(event) => onClickFavourite(event, row)}
                                      >
                                        {row.favourite ? <FavoriteYIcon /> : <FavoriteNIcon />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <Grid item xs={3}>
                                  {buttons.includes('delete') && (
                                    <Tooltip title='Delete'>
                                      <IconButton
                                        id='button'
                                        color='secondary'
                                        size='small'
                                        className={classes.iconButton}
                                        onClick={(event) => onClickDelete(event, row.materialId)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                              </Grid>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  }
                  {sortedMats.length === 0 && sortedFolders.length === 0 && (
                    <TableRow>
                      <TableCell
                        key={99}
                        className={classes.buttonCell}
                        colSpan={columns.length + 1 + !!allowSelection}
                      >
                        <Typography className={`${classes.subtleText}`}>
                          No materials matching your current filter
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 33 * emptyRows, }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[15, 50, 100]}
              component="div"
              count={visibleMats.length + visibleFolders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              nextIconButtonProps={{'style': { 'marginRight': '60px', }}} // Avoid obstruction by chat button
            />
          </>
        )}
      </Paper>
      {modalMaterialId && (
        <ShareModal
          material={modalMaterial}
          visible={showPublishModal}
          onHide={() => { setShowPublishModal(false); setModalMaterialId(null) }}
          unlock={modalMaterial?.unlocked}
          onUnlock={() => { handleUnlock(modalMaterialId) }}
          publish={modalMaterial?.publish}
          onPublish={(data) => { handlePublish(modalMaterialId, data) }}
        />
      )}
      {allowSelection && selected && selected.length >= 2 && (
        <StitchModal
          materials={getSelectedMats(materials, selected)}
          visible={showStitchModal}
          onHide={() => { setShowStitchModal(false); }}
        />
      )}
      {stitchViewModalMatProxy && (
        <StitchViewModal
          stitchedMaterialProxy={stitchViewModalMatProxy}
          visible={!!stitchViewModalMatProxy}
          onHide={() => { setStitchViewModalMatProxy(null); }}
        />
      )}
      <DeleteModal
        material={modalMaterial}
        visible={showDeleteModal}
        setVisible={setShowDeleteModal}
        onDelete={doOnDelete}
      />
    </Box>
  );
}
